import {escape, get} from 'lodash';

import {AfterViewInit, Component, ElementRef, Injector, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {EventTrackAction, LinkClicked} from '@clients/shared/analytics';

import {ISurveyHtml} from '../../../models/survey/ISurveyContent';
import {BaseElement} from '../../base-element.abstract';
import { replaceInlineStyleNonce, extractInlineStylesToCssClass } from '../../utilities/replaceNonce';
import { RunInstructionsAction } from '../../+state/blocks/blocks.actions';

@Component({
    selector: 'surveys-element-html',
    templateUrl: './element-html.component.html',
    styleUrls: ['./element-html.component.scss'],
})
export class ElementHtmlComponent extends BaseElement implements OnChanges, AfterViewInit, OnDestroy {
    @Input()
    public element: ISurveyHtml;
    @Input()
    public useCustomStyles = false;
    @ViewChild('html')
    public htmlWrapper: ElementRef;

    public sanitizedHtml: any;

    private customLinkHandler = this.handleInnerLinkClicked.bind(this);

    constructor(injector: Injector, private sanitizer: DomSanitizer) {
        super(injector);
    }

    ngOnChanges() {
        this.updateHtml();
    }

    ngAfterViewInit() {
        this.addCustomLinkHandler();
        this.injectVideoPrintFallbacks();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.removeCustomLinkHandler();
    }

    public shouldRender(element: ISurveyHtml, language: string): boolean {
        const html = this.getHtml(element, language);
        return html && html.length > 0;
    }

    public getComponentCssClasses(id: string): string {
        return !this.useCustomStyles ? `survey-element surveys-element-html surveys-element-${id}` : '';
    }

    protected languageChanged() {
        this.updateHtml();
    }

    private handleInnerLinkClicked(event) {
        /* istanbul ignore else  */
        if (event.target.localName === 'a') {
            this.store.dispatch(new EventTrackAction(new LinkClicked(event.target.href)));
        }

        const config = event.srcElement.dataset?.config;
        if(!config) return;
        const configJson = JSON.parse(config);
        if (configJson && configJson?.instructions) {
            this.store.dispatch(new RunInstructionsAction(configJson));
        }
    }

    private updateHtml() {
        // remove the custom link handler until the new HTML is calculated
        this.removeCustomLinkHandler();

        // calculate new HTML
        const html: string = this.getHtml(this.element, this.language);
        
        // Extract inline style to style tag
        const extHtml = extractInlineStylesToCssClass(html);
        
        // Inject the nonce into any <style> tags in the HTML string
        const updatedHtml = replaceInlineStyleNonce(extHtml);
        
        if (!html) return;

        this.sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(updatedHtml);

        // add the link handler back in
        this.addCustomLinkHandler();
        this.injectVideoPrintFallbacks();
    }

    private getHtml(element, language: string): string {
        return get(element, `data.${language}`);
    }

    private addCustomLinkHandler() {
        if (this.htmlWrapper) {
            this.htmlWrapper.nativeElement.addEventListener('click', this.customLinkHandler, false);
        }
    }

    private removeCustomLinkHandler() {
        if (this.htmlWrapper) {
            this.htmlWrapper.nativeElement.removeEventListener('click', this.customLinkHandler, false);
        }
    }

    private injectVideoPrintFallbacks(): void {
        setTimeout(() => {
            const root = this.htmlWrapper?.nativeElement as HTMLElement | undefined;
            if (!root) return;

            root.querySelectorAll('.video-print-fallback').forEach((el) => el.remove());

            const title = this.language === 'es' ? 'Vídeo' : 'Video';
            for (const iframe of Array.from(root.querySelectorAll<HTMLIFrameElement>('.video-wrapper iframe'))) {
                const wrap = iframe.closest('.video-wrapper');
                const url = iframe.getAttribute('src')?.trim();
                if (!wrap || !url) continue;

                const fb = document.createElement('div');
                fb.className = 'video-print-fallback';
                fb.innerHTML = `<div class="video-print-fallback__title">${escape(title)}</div><a class="video-print-fallback__link" href="${escape(url)}" target="_blank" rel="noopener noreferrer">${escape(url)}</a>`;
                wrap.appendChild(fb);
            }
        }, 0);
    }
}
